import React from 'react';
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';

// Get base URL for public assets
const getPublicUrl = (path: string) => {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
};

const DEFAULT_LOGO = 'https://i.postimg.cc/QVPV9d0x/Full-name-logo-blue.png';
const CREST_LOGO = 'https://i.postimg.cc/MTvXhZ7K/image-12.png';
const CREST_PENTEST_BADGE = 'https://i.postimg.cc/PxPNBfWp/image-13.png';
const CERTIFIED_ICON = getPublicUrl('/achievement-icon.webp');

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    fontFamily: 'Roboto',
    position: 'relative',
  },
  mainContent: {
    flex: 1,
    paddingTop: 50,
    paddingHorizontal: 40,
    paddingBottom: 20,
  },
  greenAccent: {
    position: 'absolute',
    left: 0,
    top: 35,
    width: 5,
    height: 90,
    backgroundColor: '#84cc16',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: 24,
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 11.5,
    color: '#475569',
    lineHeight: 1.60,
    marginBottom: 12,
    textAlign: 'justify',
  },
  certifiedSection: {
    flexDirection: 'row',
    marginTop: 40,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 15,
  },
  certifiedIcon: {
    width: 32,
    height: 32,
    marginTop: 2,
  },
  certifiedLeft: {
    width: '38%',
  },
  certifiedTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: 6,
  },
  certifiedSubtitle: {
    fontSize: 9,
    color: '#475569',
    marginBottom: 8,
  },
  certifiedText: {
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.30,
  },
  badgesContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 15,
    marginLeft: 30,
  },
  badgeWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeBox: {
    width: 75,
    height: 75,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  badgeShadow: {
    backgroundColor: '#f1f5f9',
    borderRadius: 14,
    padding: 4,
  },
  crestLogo: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  crestPentestBadge: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  badgeLabel: {
    fontSize: 8.5,
    color: '#1e3a5f',
    marginTop: 8,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  footer: {
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
    paddingVertical: 20,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 60,
  },
  footerColumn: {
    flexDirection: 'column',
  },
  footerTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1e3a5f',
    marginBottom: 8,
  },
  footerText: {
    fontSize: 8.5,
    color: '#4B5563',
    marginBottom: 4,
  },
  footerAddressText: {
    fontSize: 8.5,
    color: '#4b5563',
    marginBottom: 2,
  },
  footerLogoWrapper: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  footerLogo: {
    width: 100,
    height: 35,
    objectFit: 'contain',
  },
  pageNumFooter: {
    position: 'absolute',
    bottom: 8,
    right: 40,
    fontSize: 8,
    color: '#9CA3AF',
  },
});

const AboutUsPageV2: React.FC = () => (
  <Page size="A4" style={styles.page}>
    {/* Green accent bar */}
    <View style={styles.greenAccent} />

    {/* Main Content */}
    <View style={styles.mainContent}>
      <Text style={styles.title}>About Us</Text>

      <Text style={styles.sectionTitle}>Modern Penetration Testing as a Service</Text>

      <Text style={styles.paragraph}>
        Capture The Bug is a modern Penetration Testing as a Service (PTaaS) platform designed to support fast-moving engineering teams and security-conscious businesses.
      </Text>

      <Text style={styles.paragraph}>
        Built for agility, collaboration, and scale, our platform combines human-led expertise with automation to continuously identify, validate, and triage security vulnerabilities across web, mobile, infra and API environments.
      </Text>

      <Text style={styles.paragraph}>
        Organisations across New Zealand, Australia, and North America - including public companies and high-growth SaaS teams - trust us to secure their CI/CD pipelines, meet compliance goals, and reduce time-to-remediation without slowing development velocity.
      </Text>

      {/* Certified & Trusted Section */}
      <View style={styles.certifiedSection}>
        <Image src={CERTIFIED_ICON} style={styles.certifiedIcon} />

        <View style={styles.certifiedLeft}>
          <Text style={styles.certifiedTitle}>Certified & Trusted</Text>
          <Text style={styles.certifiedSubtitle}>Industry-leading standards</Text>
          <Text style={styles.certifiedText}>
            Capture The Bug is headquartered in New Zealand and proudly works with customers across regulated and innovation-driven sectors.
          </Text>
        </View>

        <View style={styles.badgesContainer}>
          <View style={styles.badgeWrapper}>
            <View style={styles.badgeShadow}>
              <View style={styles.badgeBox}>
                <Image src={CREST_LOGO} style={styles.crestLogo} />
              </View>
            </View>
            <Text style={styles.badgeLabel}>CREST Certified</Text>
          </View>
          <View style={styles.badgeWrapper}>
            <View style={styles.badgeShadow}>
              <View style={styles.badgeBox}>
                <Image src={CREST_PENTEST_BADGE} style={styles.crestPentestBadge} />
              </View>
            </View>
            <Text style={styles.badgeLabel}>Industry Standards</Text>
          </View>
        </View>
      </View>
    </View>

    {/* Footer */}
    <View style={styles.footer}>
      <View style={styles.footerColumn}>
        <Text style={styles.footerTitle}>Contact Information</Text>
        <Text style={styles.footerText}>hello@capturethebug.xyz</Text>
        <Text style={styles.footerText}>ankita@capturethebug.xyz</Text>
        <Text style={styles.footerText}>+64 22 199 4320</Text>
      </View>

      <View style={styles.footerColumn}>
        <Text style={styles.footerTitle}>Address</Text>
        <Text style={styles.footerAddressText}>526 Victoria Street Waikato,</Text>
        <Text style={styles.footerAddressText}>Hamilton, New Zealand</Text>
      </View>

      <View style={styles.footerLogoWrapper}>
        <Image src={DEFAULT_LOGO} style={styles.footerLogo} />
      </View>
    </View>
  </Page>
);

export default AboutUsPageV2;
